const API_URL = (import.meta.env["VITE_API_URL"] as string | undefined) ?? "https://api.structurify.dscvry.ai";

const SESSION_KEY = "structurify_session";

export interface AuthUser {
  email: string;
  userId: string;
  creditsUsed: number;
  plan: string;
  session: string;
}

// ─── Session persistence ──────────────────────────────────────────────────────

export function saveSession(user: AuthUser): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(user));
}

export function loadSession(): AuthUser | null {
  const raw = localStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw) as AuthUser; } catch { return null; }
}

export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
}

// ─── API calls ────────────────────────────────────────────────────────────────

export async function requestMagicLink(email: string): Promise<void> {
  const res = await fetch(`${API_URL}/api/auth/magic`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const data = await res.json() as { error?: string };
    throw new Error(data.error ?? `Request failed: ${res.status}`);
  }
}

export async function verifyMagicToken(token: string): Promise<AuthUser> {
  const res = await fetch(`${API_URL}/api/auth/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) {
    const data = await res.json() as { error?: string };
    throw new Error(data.error ?? `Verification failed: ${res.status}`);
  }
  return res.json() as Promise<AuthUser>;
}

export async function fetchMe(session: string): Promise<AuthUser | null> {
  const res = await fetch(`${API_URL}/api/auth/me`, {
    headers: { "Authorization": `Bearer ${session}` },
  });
  if (res.status === 401) return null;
  if (!res.ok) return null;
  const data = await res.json() as Omit<AuthUser, "session">;
  return { ...data, session };
}

export async function logout(session: string): Promise<void> {
  await fetch(`${API_URL}/api/auth/logout`, {
    method: "POST",
    headers: { "Authorization": `Bearer ${session}` },
  });
}

export function getAuthHeaders(session: string | null): Record<string, string> {
  if (!session) return {};
  return { "Authorization": `Bearer ${session}` };
}
